<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useStore } from '@/store'
import {
  ConnectorConfig,
  connectorsConnexionInputs,
  connectorsTypesLabels,
  ConnectorType,
  defaultExternalName
} from '@/store/modules/connector-config/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

import AppFinessInput from './AppFinessInput.vue'
import ConnectorFlowForm from './ConnectorFlowForm.vue'

export interface ConnectorFormProps {
  index: number
  connector: ConnectorConfig
}

const store = useStore()
const { t } = useI18n()
const props = defineProps<ConnectorFormProps>()

const connectorData = reactive({ ...props.connector })
const showDeleteDropdown = ref(false)
const requestSuccess = ref(false)
const isSubmitting = ref(false)
const isTestingCredentials = ref(false)
const credentialsValid = ref<boolean | null>(null)
const credentialsError = ref<string | null>(null)
const hasBeenEdited = ref(false)
const hasBeenSaved = ref(false)
const errorMessage = ref('')
const passwordIsEdited = ref(false)
const apiKeyIsEdited = ref(false)

enum authenticationMethodEnum {
  API_KEY = 'apiKey',
  USER_PASSWORD = 'userPassword',
}
function initialAuthenticationMethod(): authenticationMethodEnum | null {
  if (props.connector.apiKey || props.connector.apiKeyPart) {
    return authenticationMethodEnum.API_KEY
  }
  if (props.connector.username !== null || props.connector.password !== null) {
    return authenticationMethodEnum.USER_PASSWORD
  }
  return null
}
// set as the ref's initial value (not via onMounted) so the watcher below, which resets the
// fields of the other auth method, only reacts to the user actually switching method
const authenticationMethod = ref<authenticationMethodEnum | null>(initialAuthenticationMethod())
const availableAuthenticationMethods = computed(() => {
  const methods = []
  if (connectorsConnexionInputs['apiKey'].includes(connectorData.connectorType)) {
    methods.push(authenticationMethodEnum.API_KEY)
  }
  if (connectorsConnexionInputs['username'].includes(connectorData.connectorType) || connectorsConnexionInputs['password'].includes(connectorData.connectorType)) {
    methods.push(authenticationMethodEnum.USER_PASSWORD)
  }
  return methods
})

watch(authenticationMethod, () => {
  if (authenticationMethod.value === authenticationMethodEnum.API_KEY) {
    connectorData.username = null
    connectorData.password = null
  }
  if (authenticationMethod.value === authenticationMethodEnum.USER_PASSWORD) {
    connectorData.apiKey = null
    connectorData.apiKeyPart = null
  }
})
watch(() => props.connector, (newConnector) => {
  Object.assign(connectorData, newConnector)
})
watch(connectorData, function(newConnector, oldConnector) {
  hasBeenEdited.value = !_.isEqual(connectorData, props.connector)
  credentialsValid.value = null
  credentialsError.value = null
  errorMessage.value = ''

  // reset this value only if a form value has changed
  hasBeenSaved.value = _.isEqual(connectorData, props.connector) ? hasBeenSaved.value : false

  if (connectorsConnexionInputs['externalName'].includes(connectorData.connectorType) && oldConnector.connectorType !== newConnector.connectorType) {
    connectorData.externalName = defaultExternalName[connectorData.connectorType]
  }
})

function displayPassowrdInput() {
  return !props.connector.id // if connector is new password can't be set
    || props.connector.connectorType !== connectorData.connectorType // if connector type has changed password can't be set
    || passwordIsEdited.value
}

function startPasswordEdition() {
  passwordIsEdited.value = true
  connectorData.password = ''
}

function resetPasswordEdition() {
  passwordIsEdited.value = false
  connectorData.password = null
}

function displayApiKeyInput() {
  return !props.connector.apiKeyPart
    || apiKeyIsEdited.value
}

function startApiKeyEdition() {
  apiKeyIsEdited.value = true
  connectorData.apiKey = ''
}

function resetApiKeyEdition() {
  apiKeyIsEdited.value = false
  connectorData.apiKey = null
}

function addFiness({ finess, shortDescription, settingName }) {
  const finessList = settingName === 'restricted_legal_finess'
    ? connectorData.legalFinessList
    : connectorData.geoFinessList

  if (!finessList.find((elem) => elem.finess === finess)) {
    if(settingName === 'restricted_legal_finess') {
      connectorData.legalFinessList = [...finessList, { finess, shortDescription }]
    } else {
      connectorData.geoFinessList = [...finessList, { finess, shortDescription }]
    }
  }
}

function removeFiness(finess, settingName) {
  if(settingName === 'restricted_legal_finess') {
    connectorData.legalFinessList = connectorData.legalFinessList.filter((elem) => elem.finess !== finess)
  } else {
    connectorData.geoFinessList = connectorData.geoFinessList.filter((elem) => elem.finess !== finess)
  }
}

function updateFlow(flowIndex, flowData) {
  const connectorFlows = [...connectorData.flows]
  connectorFlows[flowIndex] = flowData
  connectorData.flows = connectorFlows
}

function deleteFlow(flowIndex) {
  const connectorFlows = [...connectorData.flows]
  connectorFlows.splice(flowIndex, 1)
  connectorData.flows = connectorFlows
}

async function submitConnector() {
  if(!hasBeenEdited.value) {
    return
  }

  isSubmitting.value = true
  requestSuccess.value = false

  // reset unavailable fields based on connector type
  for(const field in connectorsConnexionInputs) {
    if(field in connectorData && !connectorsConnexionInputs[field].includes(connectorData.connectorType)) {
      connectorData[field] = null
    }
  }
  if (connectorsConnexionInputs['externalName'].includes(connectorData.connectorType) && connectorData.externalName === '') {
    errorMessage.value = t('connectorConfig.externalNameRequired')
    return
  }

  const requestName = props.connector.id ? 'putConnectorConfig' : 'postConnectorConfig'
  // hack to not break test checking password value
  const connectorDataCopy = { ...connectorData }
  const response = await store.dispatch('connectorConfig/submitConnectorConfig', { connectorIndex: props.index, connectorData: connectorDataCopy })
  Object.assign(connectorData, response)
  isSubmitting.value = false
  hasBeenEdited.value = false
  hasBeenSaved.value = store.state.connectorConfig.requests[requestName].ok
  errorMessage.value = store.state.connectorConfig.requests[requestName].error?.message || ''
  resetPasswordEdition()
  resetApiKeyEdition()
}

function deleteConnector() {
  showDeleteDropdown.value = false

  if(!props.connector.id) {
    store.commit('connectorConfig/deleteConnectorConfigSuccess', props.index)
  } else {
    store.dispatch('connectorConfig/deleteConnectorConfig', { connectorId: props.connector.id, connectorIndex: props.index })
  }
}

function testCredentials() {
  isTestingCredentials.value = true
  credentialsValid.value = null
  credentialsError.value = null
  store.dispatch('connectorConfig/testCredentials', props.connector.id).then((response) => {
    credentialsValid.value = response?.['valid'] ?? false
    credentialsError.value = response?.['error'] ?? null
  }).finally(() => {
    isTestingCredentials.value = false
  })
}

function newEmptyFlow() {
  const newFlow = { id: null, type: null, path: '', formatVersion: '', pmsiType: PmsiTypeEnum.MCO }
  connectorData.flows = [...connectorData.flows, newFlow]
}

</script>

<template>
  <div class="card p-0 shadow mx-3 my-4">
    <ul class="d-flex p-0 m-0 overflow-hidden">
      <li class="card rounded-bottom-0 rounded-top-0 col-3 list-group-item mb-2 border-0 border-bottom">
        <div class="row g-0 mx-4 ">
          <div class="col">
            <div class="row g-0">
              <div class="col pb-2 my-3 border-bottom">
                <strong>Connexion</strong>
              </div>
            </div>
            <div class="mb-3">
              <label>Type de connecteur</label>
              <select
                v-model="connectorData.connectorType"
                class="form-select"
              >
                <option
                  :value="null"
                >
                  --- Choisissez un type ---
                </option>
                <option
                  v-for="(label, value) in connectorsTypesLabels"
                  :key="value"
                  :value="value"
                >
                  {{ label }}
                </option>
              </select>
            </div>
            <div
              v-if="connectorsConnexionInputs['externalName'].includes(connectorData.connectorType)"
              class="mb-3"
            >
              <label>Nom du logiciel PMSI</label>
              <input
                v-model="connectorData.externalName"
                type="text"
                class="form-control"
              >
            </div>
            <div
              v-if="connectorsConnexionInputs['host'].includes(connectorData.connectorType)"
              class="mb-3"
            >
              <label>Hôte</label>
              <input
                v-model="connectorData.host"
                type="text"
                class="form-control"
              >
            </div>
            <div
              v-if="connectorsConnexionInputs['isSecureSsl'].includes(connectorData.connectorType)"
              class="mb-3"
            >
              <label>Vérifier le certificat SSL</label>
              <select
                v-model="connectorData.isSecureSsl"
                class="form-select"
              >
                <option :value="true">
                  {{ t('yes') }}
                </option>
                <option :value="false">
                  {{ t('no') }}
                </option>
              </select>
            </div>
            <div
              v-if="availableAuthenticationMethods.length > 1"
              class="mb-3"
            >
              <label for="authentication_type">{{ t('connectorConfig.authenticationType') }}</label>
              <select
                id="authentication_type"
                v-model="authenticationMethod"
                class="form-select"
                name="authentication_type"
              >
                <option
                  v-for="method in availableAuthenticationMethods"
                  :key="method"
                  :value="method"
                >
                  {{ t('connectorConfig.'+method) }}
                </option>
              </select>
            </div>
            <div
              v-if="connectorsConnexionInputs['apiKey'].includes(connectorData.connectorType) && (availableAuthenticationMethods.length === 1 || authenticationMethod === authenticationMethodEnum.API_KEY)"
              class="mb-3"
            >
              <label>Clé d'API</label>
              <button
                v-if="!displayApiKeyInput()"
                id="connector_api_key"
                type="button"
                class="btn btn-link btn-sm float-end"
                @click="startApiKeyEdition"
              >
                {{ t('modify') }}
              </button>
              <button
                v-if="apiKeyIsEdited"
                type="button"
                class="btn btn-link btn-sm float-end"
                @click="resetApiKeyEdition"
              >
                {{ t('cancel') }}
              </button>
              <div>
                <div
                  v-if="!displayApiKeyInput()"
                  class="alert alert-secondary py-1 px-2"
                >
                  {{ connectorData.apiKeyPart }}
                </div>
                <input
                  v-if="displayApiKeyInput()"
                  ref="apiKeyInput"
                  v-model="connectorData.apiKey"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <template v-if="availableAuthenticationMethods.length === 1 || authenticationMethod === authenticationMethodEnum.USER_PASSWORD">
              <div
                v-if="connectorsConnexionInputs['username'].includes(connectorData.connectorType)"
                class="mb-3"
              >
                <label for="connector_username">{{ t('user.user') }}</label>
                <input
                  id="connector_username"
                  v-model="connectorData.username"
                  type="text"
                  class="form-control"
                  name="connector_username"
                >
              </div>
              <div
                v-if="connectorsConnexionInputs['password'].includes(connectorData.connectorType)"
                class="mb-3"
              >
                <label>{{ t('user.password') }}</label>
                <button
                  v-if="!displayPassowrdInput()"
                  class="btn btn-link btn-sm float-end"
                  @click="startPasswordEdition"
                >
                  {{ t('modify') }}
                </button>
                <button
                  v-if="passwordIsEdited"
                  type="button"
                  class="btn btn-link btn-sm float-end"
                  @click="resetPasswordEdition"
                >
                  {{ t('cancel') }}
                </button>
                <div>
                  <div
                    v-if="!displayPassowrdInput()"
                    class="alert alert-secondary py-1 px-2"
                  >
                    Mot de passe masqué
                  </div>
                  <input
                    v-if="displayPassowrdInput()"
                    id="connector_password"
                    ref="passwordInput"
                    v-model="connectorData.password"
                    type="password"
                    class="form-control"
                  >
                </div>
              </div>
            </template>
            <div
              v-if="connectorData.connectorType === ConnectorType.WEBPIMS && connector.id && authenticationMethod === authenticationMethodEnum.USER_PASSWORD"
              class="d-flex justify-content-between align-items-center mt-1"
            >
              <button
                type="button"
                class="btn btn-link btn-sm"
                :disabled="isTestingCredentials || hasBeenEdited"
                @click="testCredentials"
              >
                {{ t('connectorConfig.testCredentials') }}
              </button>
              <div
                v-if="isTestingCredentials"
                class="loader loader-sm loader-center mx-0 my-1"
                style="width: 18px; height: 18px;"
              />
              <small
                v-if="credentialsValid === true"
                class="text-success"
              >
                {{ t('connectorConfig.credentialsValid') }}
              </small>
              <small
                v-if="credentialsValid === false"
                class="text-danger"
              >
                {{ credentialsError ?? t('connectorConfig.credentialsInvalid') }}
              </small>
            </div>
          </div>
        </div>
      </li>
      <li class="card rounded-bottom-0 rounded-top-0 col-3 list-group-item border border-top-0 mb-2">
        <div class="row g-0 mx-4">
          <div class="pb-2 my-3 border-bottom">
            <strong>Filtrer par finess</strong>
          </div>
          <div
            class="mb-3"
          >
            <select
              v-model="connectorData.isAllFiness"
              class="form-select"
            >
              <option :value="true">
                Autoriser tous les finess
              </option>
              <option :value="false">
                Filtrer par finess
              </option>
            </select>
          </div>
          <div
            v-if="!connectorData.isAllFiness"
            class="mb-3"
          >
            <label>Finess juridique</label>
            <div>
              <div
                v-for="finess in connectorData.legalFinessList"
                :key="finess['finess']"
                class="text-bg-primary badge col-auto mx-1 mb-1 px-2 text-wrap"
              >
                <span>{{ finess['shortDescription'] }} ({{ finess['finess'] }})</span>
                <span
                  class="criteria-remove"
                  @click="removeFiness(finess['finess'], 'restricted_legal_finess')"
                >
                  <sancare-octicon
                    name="x"
                    :width="14"
                    :height="11"
                  />
                </span>
              </div>
            </div>
            <app-finess-input
              :setting-name="'restricted_legal_finess'"
              @select-finess="addFiness"
            />
          </div>
          <div
            v-if="!connectorData.isAllFiness"
            class="mb-3"
          >
            <label>Finess géographique</label>
            <div>
              <div
                v-for="finess in connectorData.geoFinessList"
                :key="finess['finess']"
                class="text-bg-primary badge col-auto mx-1 mb-1 ps-2 pe-2"
              >
                <span>{{ finess['shortDescription'] }} ({{ finess['finess'] }})</span>
                <span
                  class="criteria-remove"
                  @click="removeFiness(finess['finess'], 'restricted_geo_finess')"
                >
                  <sancare-octicon
                    name="x"
                    :width="14"
                    :height="11"
                  />
                </span>
              </div>
            </div>
            <app-finess-input
              :setting-name="'restricted_geo_finess'"
              @select-finess="addFiness"
            />
          </div>
        </div>
      </li>
      <li class="card rounded-bottom-0 rounded-top-0 col-6 list-group-item flex-grow-1 mb-2 border-0 border-bottom">
        <div class="row g-0 mx-4">
          <div class="pb-2 my-3 border-bottom">
            <strong>Liste des flux</strong>
          </div>
          <div
            v-if="connectorData.flows.length === 0"
            class="alert alert-info text-center"
          >
            Attention aucun flux n'est configuré
          </div>
          <connector-flow-form
            v-for="(flow, flowIndex) in connectorData.flows"
            :key="flow.id"
            :index="flowIndex"
            :connector-index="index"
            :connector-type="connectorData.connectorType"
            :flow="flow"
            @update-flow="updateFlow"
            @delete-flow="deleteFlow"
          />
          <button
            type="button"
            class="btn btn-block btn-outline-primary"
            @click="newEmptyFlow"
          >
            Ajouter un flux
          </button>
        </div>
      </li>
    </ul>
    <div class="card-footer list-group-item d-flex justify-content-between mx-4 my-2 mb-3">
      <v-dropdown
        placement="top"
        class="float-end"
        :shown="showDeleteDropdown"
        @hide="!showDeleteDropdown"
      >
        <button
          type="button"
          class="btn btn-outline-danger"
          @click="showDeleteDropdown = true"
        >
          Supprimer le connecteur
        </button>
        <template #popper>
          <div class="p-3">
            <h4 class="title-light text-center">
              {{ t('areYouSure') }}
            </h4>
            <div class="text-center">
              <button
                class="btn btn-danger btn-padded"
                @click="deleteConnector()"
              >
                {{ t('delete') }}
              </button>
            </div>
          </div>
        </template>
      </v-dropdown>
      <div class="d-flex align-items-center">
        <span
          v-if="errorMessage !== ''"
          class="me-4 text-danger"
        >
          {{ errorMessage }}
        </span>
        <span
          v-else-if="hasBeenSaved"
          class="me-4 text-success"
        >
          Les modifications ont bien été enregistrées
        </span>
        <span
          v-else-if="connector && hasBeenEdited && !isSubmitting"
          class="me-4"
        >
          Des modifications sont à enregistrer
        </span>
        <div
          v-if="isSubmitting"
          class="loader loader-sm loader-center m-0 me-2"
        />
        <button
          id="connector_submit"
          type="button"
          class="btn btn-primary"
          :disabled="!hasBeenEdited || isSubmitting"
          @click="submitConnector()"
        >
          Enregistrer le connecteur
        </button>
      </div>
    </div>
  </div>
</template>
